import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PolarisBuildInfo } from '@cassiopeia/integration';

export interface SystemVersion extends PolarisBuildInfo {
  polaris?: PolarisBuildInfo;
  showVersionMismatch: boolean;
}

/** Both halves of the backend are built from this repository. */
const commitUrl = (commitSHA: string) => `https://github.com/dotnestech/polaris/commits/${commitSHA}`;

@Injectable({ providedIn: 'root' })
export class SystemVersionService {
  #http = inject(HttpClient);
  #version = signal<SystemVersion>({ showVersionMismatch: false } as SystemVersion);

  readonly version = this.#version.asReadonly();

  /** The two builds when they disagree, otherwise undefined — differing commits mean a partial deploy. */
  readonly polarisMismatch = computed(() => {
    const { commitSHA, branch, polaris, showVersionMismatch } = this.#version();
    if (!commitSHA || !polaris?.commitSHA || commitSHA === polaris.commitSHA || !showVersionMismatch) return undefined;
    return {
      cassiopeia: { branch, commitSHA },
      polaris: { branch: polaris.branch, commitSHA: polaris.commitSHA },
    };
  });

  constructor() {
    this.#http.get<SystemVersion>('/api/environment/system/version').subscribe(version => {
      this.#version.set(version);
      const mismatch = this.polarisMismatch();
      if (mismatch) {
        const { cassiopeia, polaris: polarisBuild } = mismatch;
        console.error(
          `Backend version mismatch: the Cassiopeia server and Polaris are running different builds.
Cassiopeia server: ${cassiopeia.branch} ${commitUrl(cassiopeia.commitSHA)}
Polaris: ${polarisBuild.branch} ${commitUrl(polarisBuild.commitSHA)}`,
        );
      }
    });
  }
}
